import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, getApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useMessages } from '../../Messages';

const calcSessionHours = (start, end) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) return null;
  return (diff / 60).toFixed(2);
};

const PrintingTaskInterface = ({ task, onTaskAction, onComplete, readOnly = false }) => {
  const { t } = useTranslation('operator');
  const { user } = useAuth();
  const { showError } = useMessages();
  const [printingData, setPrintingData] = useState({
    printResolution: '',
    printSpeed: '',
    inkConsumption: '',
    colorProfile: '',
    printedQuantity: 0,
    wasteQuantity: 0,
    qualityIssues: '',
    notes: ''
  });

  const [printStatus, setPrintStatus] = useState(
    task.status === 'IN_PROGRESS' ? 'printing' : 'ready'
  ); // ready, printing, paused, completed
  const [qualityChecks, setQualityChecks] = useState([]);
  const [currentRun, setCurrentRun] = useState({
    startTime: null,
    targetQuantity: task.order_quantity || 1000,
    actualQuantity: 0
  });

  // Operator sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Roll consumption tracking state
  const [rolls, setRolls] = useState([]);
  const [currentRoll, setCurrentRoll] = useState({
    roll_sequence: 1,
    roll_barcode: '',
    initial_weight_kg: '',
    final_weight_kg: '',
    final_meters: '',
    waste_category: '',
    waste_notes: ''
  });
  const [showRollForm, setShowRollForm] = useState(false);
  const [editingRollId, setEditingRollId] = useState(null);
  const [loadingRolls, setLoadingRolls] = useState(false);

  // Quality checkpoints (could be loaded from task config)
  const checkpoints = [
    { interval: 50, key: 'colorDensity', description: 'Color density check', completed: false },
    { interval: 100, key: 'registration', description: 'Registration verification', completed: false },
    { interval: 200, key: 'printQuality', description: 'Print quality assessment', completed: false },
    { interval: 500, key: 'materialFeed', description: 'Material feed inspection', completed: false }
  ];

  const [checkpointStatus, setCheckpointStatus] = useState(checkpoints);

  const loadSessions = useCallback(async () => {
    if (!task?.task_id) return;
    try {
      setLoadingSessions(true);
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/printing-sessions`));
      if (res.ok) {
        const data = await res.json();
        setSessions(data || []);
      }
    } catch (err) {
      console.error('Error loading printing sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  }, [task?.task_id]);

  // Load existing rolls when component mounts
  useEffect(() => {
    if (task?.task_id) {
      loadRolls();
      loadNextRollSequence();
      loadSessions();
    }
  }, [task?.task_id]);

  // Auto-activate printing controls when task moves to IN_PROGRESS
  useEffect(() => {
    if (task.status === 'IN_PROGRESS' && printStatus === 'ready') {
      setPrintStatus('printing');
    }
  }, [task.status]);

  // Check quality checkpoints based on printed quantity
  useEffect(() => {
    const updatedCheckpoints = checkpointStatus.map(checkpoint => ({
      ...checkpoint,
      due: printingData.printedQuantity >= checkpoint.interval,
      overdue: printingData.printedQuantity > checkpoint.interval + 20 && !checkpoint.completed
    }));
    setCheckpointStatus(updatedCheckpoints);
  }, [printingData.printedQuantity]);

  // Calculate total waste from rolls
  useEffect(() => {
    if (rolls.length > 0) {
      const totalWaste = rolls.reduce((sum, roll) => sum + (roll.waste_weight_kg || 0), 0);
      const totalInput = rolls.reduce((sum, roll) => sum + (roll.initial_weight_kg || 0), 0);
      setPrintingData(prev => ({
        ...prev,
        wasteQuantity: totalWaste.toFixed(2),
        // Update printed quantity from final weights if desired
      }));
    }
  }, [rolls]);

  const handleDeleteSession = async (sessionId) => {
    // Sessions are now read-only — this function is kept as a no-op for safety
    console.warn('Session deletion is disabled; sessions are auto-created at shift handoff.');
  };

  const loadRolls = async () => {
    try {
      setLoadingRolls(true);
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/rolls`));
      if (response.ok) {
        const data = await response.json();
        setRolls(data || []);
      }
    } catch (error) {
      console.error('Error loading rolls:', error);
    } finally {
      setLoadingRolls(false);
    }
  };

  const loadNextRollSequence = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/next-roll-sequence`));
      if (response.ok) {
        const data = await response.json();
        setCurrentRoll(prev => ({ ...prev, roll_sequence: data.next_sequence }));
      }
    } catch (error) {
      console.error('Error loading next roll sequence:', error);
    }
  };

  const handleAddRoll = async () => {
    // Validation
    if (!currentRoll.initial_weight_kg || !currentRoll.final_weight_kg) {
      showError(t('printingTaskInterface.toasts.missingFieldsTitle'), t('printingTaskInterface.toasts.missingFieldsMessage'));
      return;
    }

    try {
      const rollData = {
        task_id: task.task_id,
        roll_sequence: currentRoll.roll_sequence,
        roll_barcode: currentRoll.roll_barcode || null,
        initial_weight_kg: parseFloat(currentRoll.initial_weight_kg),
        final_weight_kg: parseFloat(currentRoll.final_weight_kg),
        final_meters: currentRoll.final_meters ? parseFloat(currentRoll.final_meters) : null,
        waste_category: currentRoll.waste_category || null,
        waste_notes: currentRoll.waste_notes || null,
        recorded_by: user?.user_id
      };

      let response;
      if (editingRollId) {
        response = await apiFetch(
          getApiUrl(`/api/production-tasks/rolls/consumption/${editingRollId}`),
          { method: 'PUT', body: JSON.stringify(rollData) }
        );
      } else {
        response = await apiFetch(
          getApiUrl('/api/production-tasks/rolls/consumption'),
          { method: 'POST', body: JSON.stringify(rollData) }
        );
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showError(t('printingTaskInterface.toasts.saveFailedTitle'), err.detail || err.message || t('printingTaskInterface.toasts.saveFailed'));
        return;
      }

      // Reload rolls and reset form
      await loadRolls();
      await loadNextRollSequence();
      resetRollForm();
      setShowRollForm(false);

      // Auto-calculate waste record
      await calculateWasteFromRolls();
    } catch (error) {
      console.error('Error saving roll:', error);
      showError(t('common:ui.networkError'), t('printingTaskInterface.toasts.saveFailed'));
    }
  };

  const handleEditRoll = (roll) => {
    setCurrentRoll({
      roll_sequence: roll.roll_sequence,
      roll_barcode: roll.roll_barcode || '',
      initial_weight_kg: roll.initial_weight_kg,
      final_weight_kg: roll.final_weight_kg,
      final_meters: roll.final_meters || '',
      waste_category: roll.waste_category || '',
      waste_notes: roll.waste_notes || ''
    });
    setEditingRollId(roll.roll_consumption_id);
    setShowRollForm(true);
  };

  const handleDeleteRoll = async (rollId) => {
    if (!window.confirm(t('printingTaskInterface.confirmDeleteRoll'))) {
      return;
    }

    try {
      const response = await apiFetch(
        getApiUrl(`/api/production-tasks/rolls/consumption/${rollId}`),
        { method: 'DELETE' }
      );
      if (response.ok) {
        await loadRolls();
        await loadNextRollSequence();
        await calculateWasteFromRolls();
      } else {
        showError(t('printingTaskInterface.toasts.deleteFailedTitle'), t('printingTaskInterface.toasts.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting roll:', error);
      showError(t('common:ui.networkError'), t('printingTaskInterface.toasts.deleteFailed'));
    }
  };

  const calculateWasteFromRolls = async () => {
    try {
      await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${task.task_id}/calculate-waste`),
        { method: 'POST' }
      );
    } catch (error) {
      console.error('Error calculating waste:', error);
    }
  };

  const resetRollForm = () => {
    setCurrentRoll({
      roll_sequence: currentRoll.roll_sequence + 1,
      roll_barcode: '',
      initial_weight_kg: '',
      final_weight_kg: '',
      final_meters: '',
      waste_category: '',
      waste_notes: ''
    });
    setEditingRollId(null);
  };

  const handleInputChange = (field, value) => {
    setPrintingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRollInputChange = (field, value) => {
    setCurrentRoll(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePausePrinting = () => {
    setPrintStatus('paused');
  };

  const handleResumePrinting = () => {
    setPrintStatus('printing');
  };

  const handleStopPrinting = () => {
    setPrintStatus('completed');
  };

  const handleQualityCheck = (checkpointIndex, passed, notes) => {
    const updatedCheckpoints = [...checkpointStatus];
    updatedCheckpoints[checkpointIndex] = {
      ...updatedCheckpoints[checkpointIndex],
      completed: true,
      passed,
      notes,
      checkedAt: new Date().toISOString()
    };
    setCheckpointStatus(updatedCheckpoints);

    // Add to quality checks log
    setQualityChecks(prev => [...prev, {
      checkpoint: updatedCheckpoints[checkpointIndex],
      quantity: printingData.printedQuantity,
      passed,
      notes
    }]);
  };

  const handleCompletePrinting = () => {
    if (printStatus === 'completed' && printingData.printedQuantity > 0) {
      onComplete({
        taskId: task.task_id,
        printingData,
        qualityChecks,
        currentRun,
        rolls,  // Include roll consumption data
        completedAt: new Date().toISOString(),
        totalDuration: currentRun.startTime ?
          Math.round((new Date() - new Date(currentRun.startTime)) / 1000 / 60) : 0
      });
    }
  };

  const getStatusColor = () => {
    switch (printStatus) {
      case 'printing': return '#28a745';
      case 'paused': return '#ffc107';
      case 'completed': return '#007bff';
      default: return '#6c757d';
    }
  };

  const getWastePercentage = () => {
    if (printingData.printedQuantity === 0) return 0;
    return ((printingData.wasteQuantity / (printingData.printedQuantity + printingData.wasteQuantity)) * 100).toFixed(1);
  };

  const getTotalWasteFromRolls = () => {
    return rolls.reduce((sum, roll) => sum + (roll.waste_weight_kg || 0), 0).toFixed(2);
  };

  const getTotalInputWeight = () => {
    return rolls.reduce((sum, roll) => sum + (roll.initial_weight_kg || 0), 0).toFixed(2);
  };

  const getTotalOutputWeight = () => {
    return rolls.reduce((sum, roll) => sum + (roll.final_weight_kg || 0), 0).toFixed(2);
  };

  const getTotalMeters = () => {
    return rolls.reduce((sum, roll) => sum + (roll.final_meters || 0), 0).toFixed(2);
  };

  return (
    <div className="printing-task-interface">
      <div className="task-header">
        <h3>🖨️ {task.task_name}</h3>
        <div className="task-type-badge printing">
          {t('taskTypes.printing')}
        </div>
        <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
          {t(`printingTaskInterface.printStatus.${printStatus}`, { defaultValue: printStatus }).toUpperCase()}
        </div>
      </div>

      <div className="task-description">
        <p>{task.task_description}</p>
        {task.instructions && (
          <div className="instructions">
            <h4>{t('labels.instructions')}</h4>
            <p>{task.instructions}</p>
          </div>
        )}
      </div>

      {/* Print Settings Section */}
      <div className="printing-section">
        <h4>{t('printingTaskInterface.printSettings')}</h4>
        <div className="settings-grid">
          <div className="setting-field">
            <label>{t('printingTaskInterface.printResolution')}</label>
            <select
              value={printingData.printResolution}
              onChange={(e) => handleInputChange('printResolution', e.target.value)}
              className="setting-select"
              disabled={readOnly}
            >
              <option value="" disabled hidden>{t('printingTaskInterface.selectResolution')}</option>
              <option value="300x300">300x300 DPI</option>
              <option value="600x600">600x600 DPI</option>
              <option value="1200x1200">1200x1200 DPI</option>
              <option value="1440x1440">1440x1440 DPI</option>
            </select>
          </div>
          <div className="setting-field">
            <label>{t('printingTaskInterface.printSpeed')}</label>
            <input
              type="number"
              value={printingData.printSpeed}
              onChange={(e) => handleInputChange('printSpeed', e.target.value)}
              placeholder={t('printingTaskInterface.examplePlaceholder', { value: 25 })}
              className="setting-input"
              disabled={readOnly}
            />
          </div>
          <div className="setting-field">
            <label>{t('printingTaskInterface.colorProfile')}</label>
            <select
              value={printingData.colorProfile}
              onChange={(e) => handleInputChange('colorProfile', e.target.value)}
              className="setting-select"
              disabled={readOnly}
            >
              <option value="" disabled hidden>{t('printingTaskInterface.selectProfile')}</option>
              <option value="Standard_CMYK">{t('printingTaskInterface.colorProfiles.standardCmyk')}</option>
              <option value="Enhanced_Colors">{t('printingTaskInterface.colorProfiles.enhancedColors')}</option>
              <option value="Security_Profile">{t('printingTaskInterface.colorProfiles.securityProfile')}</option>
              <option value="Custom_Profile">{t('printingTaskInterface.colorProfiles.customProfile')}</option>
            </select>
          </div>
          <div className="setting-field">
            <label>{t('printingTaskInterface.inkConsumption')}</label>
            <input
              type="number"
              value={printingData.inkConsumption}
              onChange={(e) => handleInputChange('inkConsumption', e.target.value)}
              placeholder={t('printingTaskInterface.examplePlaceholder', { value: 150 })}
              className="setting-input"
              step="0.1"
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {/* Operator Sessions Section (read-only — auto-created at shift handoff) */}
      <div className="printing-section session-tracking-section">
        <div className="section-header">
          <h4>⏱ {t('printingTaskInterface.operatorSessions')}</h4>
        </div>

        {/* Sessions Table */}
        {loadingSessions ? (
          <p style={{ color: '#6c757d' }}>{t('printingTaskInterface.loadingSessions')}</p>
        ) : sessions.length > 0 ? (
          <div className="sessions-list">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('printing.fields.operator')}</th>
                  <th>{t('printing.fields.date')}</th>
                  <th>{t('printing.fields.startTime')}</th>
                  <th>{t('printing.fields.endTime')}</th>
                  <th>{t('printing.fields.hoursWorked')}</th>
                  <th>{t('printing.fields.shiftType')}</th>
                  <th>{t('printing.fields.production')}</th>
                  <th>{t('printing.fields.waste')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.session_id}>
                    <td>{s.session_sequence}</td>
                    <td>{s.operator_name || s.operator_user_id}</td>
                    <td>{s.session_date}</td>
                    <td>{s.start_time}</td>
                    <td>{s.end_time}</td>
                    <td><strong>{s.hours_worked}</strong></td>
                    <td><span className="shift-badge">{t(`printing.shiftTypes.${s.shift_type}`, { defaultValue: s.shift_type })}</span></td>
                    <td>{parseFloat(s.production_output).toFixed(0)}</td>
                    <td className="waste-cell">{parseFloat(s.waste_kg || 0).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan={5}><strong>{t('printing.table.totals')}</strong></td>
                  <td><strong>{sessions.reduce((sum, s) => sum + (parseFloat(s.hours_worked) || 0), 0).toFixed(2)}</strong></td>
                  <td></td>
                  <td><strong>{sessions.reduce((sum, s) => sum + (parseFloat(s.production_output) || 0), 0).toFixed(0)}</strong></td>
                  <td><strong>{sessions.reduce((sum, s) => sum + (parseFloat(s.waste_kg) || 0), 0).toFixed(3)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>{t('printing.messages.noSessions')}</p>
          </div>
        )}
      </div>

      {/* Roll Consumption Tracking Section */}
      <div className="printing-section roll-tracking-section">
        <div className="section-header">
          <h4>📦 {t('printingTaskInterface.rollTracking')}</h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm btn-secondary"
              onClick={loadRolls}
              title={t('printingTaskInterface.refreshRollList')}
            >
              ↺ {t('buttons.refresh')}
            </button>
            {!readOnly && (
              <button
                className="btn btn-sm btn-success"
                onClick={() => {
                  resetRollForm();
                  setShowRollForm(!showRollForm);
                }}
              >
                {showRollForm ? `➖ ${t('buttons.cancel')}` : `➕ ${t('printingTaskInterface.addRoll')}`}
              </button>
            )}
          </div>
        </div>

        {/* Roll Summary Cards */}
        {rolls.length > 0 && (
          <div className="roll-summary-cards">
            <div className="summary-card">
              <div className="summary-label">{t('printingTaskInterface.totalRolls')}</div>
              <div className="summary-value">{rolls.length}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('printingTaskInterface.inputWeight')}</div>
              <div className="summary-value">{getTotalInputWeight()} kg</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('printingTaskInterface.outputWeight')}</div>
              <div className="summary-value">{getTotalOutputWeight()} kg</div>
            </div>
            <div className="summary-card warning">
              <div className="summary-label">{t('printingTaskInterface.totalWaste')}</div>
              <div className="summary-value">{getTotalWasteFromRolls()} kg</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('printingTaskInterface.totalMeters')}</div>
              <div className="summary-value">{getTotalMeters()} m</div>
            </div>
          </div>
        )}

        {/* Add/Edit Roll Form */}
        {showRollForm && !readOnly && (
          <div className="roll-form">
            <h5>{editingRollId ? t('printingTaskInterface.editRoll') : t('printingTaskInterface.addRollNumber', { number: currentRoll.roll_sequence })}</h5>
            <div className="form-grid">
              <div className="form-field">
                <label>{t('printingTaskInterface.rollBarcode')}</label>
                <input
                  type="text"
                  value={currentRoll.roll_barcode}
                  onChange={(e) => handleRollInputChange('roll_barcode', e.target.value)}
                  placeholder={t('printingTaskInterface.barcodePlaceholder')}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>{t('printingTaskInterface.initialWeight')} *</label>
                <input
                  type="number"
                  step="0.001"
                  value={currentRoll.initial_weight_kg}
                  onChange={(e) => handleRollInputChange('initial_weight_kg', e.target.value)}
                  placeholder={t('printingTaskInterface.examplePlaceholder', { value: 50.5 })}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-field">
                <label>{t('printingTaskInterface.finalWeight')} *</label>
                <input
                  type="number"
                  step="0.001"
                  value={currentRoll.final_weight_kg}
                  onChange={(e) => handleRollInputChange('final_weight_kg', e.target.value)}
                  placeholder={t('printingTaskInterface.examplePlaceholder', { value: 45.2 })}
                  className="form-input"
                  required
                />
                <small>{t('printingTaskInterface.finalWeightHint')}</small>
              </div>
              <div className="form-field">
                <label>{t('printingTaskInterface.effectiveLength')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={currentRoll.final_meters}
                  onChange={(e) => handleRollInputChange('final_meters', e.target.value)}
                  placeholder={t('printingTaskInterface.examplePlaceholder', { value: 120.5 })}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>{t('printingTaskInterface.wasteCategory')}</label>
                <select
                  value={currentRoll.waste_category}
                  onChange={(e) => handleRollInputChange('waste_category', e.target.value)}
                  className="form-select"
                >
                  <option value="" disabled hidden>{t('printingTaskInterface.selectCategory')}</option>
                  <option value="SETUP">{t('printingTaskInterface.wasteCategoryOptions.SETUP')}</option>
                  <option value="QUALITY_DEFECT">{t('printingTaskInterface.wasteCategoryOptions.QUALITY_DEFECT')}</option>
                  <option value="TRIM_SCRAP">{t('printingTaskInterface.wasteCategoryOptions.TRIM_SCRAP')}</option>
                  <option value="OTHER">{t('printingTaskInterface.wasteCategoryOptions.OTHER')}</option>
                </select>
              </div>
              <div className="form-field full-width">
                <label>{t('printingTaskInterface.wasteNotes')}</label>
                <textarea
                  value={currentRoll.waste_notes}
                  onChange={(e) => handleRollInputChange('waste_notes', e.target.value)}
                  placeholder={t('printingTaskInterface.wasteNotesPlaceholder')}
                  className="form-textarea"
                  rows="2"
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleAddRoll}>
                {editingRollId ? `💾 ${t('printingTaskInterface.updateRoll')}` : `✅ ${t('printingTaskInterface.saveRoll')}`}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  resetRollForm();
                  setShowRollForm(false);
                }}
              >
                ✖️ {t('buttons.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Rolls List */}
        {rolls.length > 0 ? (
          <div className="rolls-list">
            <table className="rolls-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('printingTaskInterface.columns.barcode')}</th>
                  <th>{t('printingTaskInterface.columns.initialKg')}</th>
                  <th>{t('printingTaskInterface.columns.finalKg')}</th>
                  <th>{t('printing.fields.waste')}</th>
                  <th>{t('printingTaskInterface.columns.meters')}</th>
                  <th>{t('printingTaskInterface.columns.category')}</th>
                  {!readOnly && <th>{t('common:ui.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {rolls.map((roll) => (
                  <tr key={roll.roll_consumption_id}>
                    <td>#{roll.roll_sequence}</td>
                    <td>{roll.roll_barcode || '-'}</td>
                    <td>{roll.initial_weight_kg}</td>
                    <td>{roll.final_weight_kg}</td>
                    <td className="waste-cell">{roll.waste_weight_kg.toFixed(3)}</td>
                    <td>{roll.final_meters || '-'}</td>
                    <td>
                      {roll.waste_category && (
                        <span className={`category-badge ${roll.waste_category.toLowerCase()}`}>
                          {t(`printingTaskInterface.wasteCategoryOptions.${roll.waste_category}`, { defaultValue: roll.waste_category.replace('_', ' ') })}
                        </span>
                      )}
                    </td>
                    {!readOnly && (
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => handleEditRoll(roll)}
                          title={t('common:ui.edit')}
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleDeleteRoll(roll.roll_consumption_id)}
                          title={t('common:ui.delete')}
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>{t('printingTaskInterface.noRolls')}</p>
          </div>
        )}
      </div>

      {/* Production Tracking */}
      <div className="printing-section">
        <h4>{t('printingTaskInterface.productionTracking')}</h4>
        <div className="production-grid">
          <div className="production-metric">
            <label>{t('printingTaskInterface.targetQuantity')}</label>
            <div className="metric-value">{currentRun.targetQuantity.toLocaleString()}</div>
          </div>
          <div className="production-metric">
            <label>{t('printingTaskInterface.printedQuantity')}</label>
            <input
              type="number"
              value={printingData.printedQuantity}
              onChange={(e) => handleInputChange('printedQuantity', parseInt(e.target.value) || 0)}
              className="quantity-input"
              min="0"
              disabled={readOnly}
            />
          </div>
          <div className="production-metric">
            <label>{t('printingTaskInterface.wasteQuantity')}</label>
            <input
              type="number"
              value={printingData.wasteQuantity}
              onChange={(e) => handleInputChange('wasteQuantity', parseInt(e.target.value) || 0)}
              className="quantity-input"
              min="0"
              disabled={readOnly}
            />
          </div>
          <div className="production-metric">
            <label>{t('printingTaskInterface.wastePercent')}</label>
            <div className={`metric-value ${getWastePercentage() > 5 ? 'warning' : ''}`}>
              {getWastePercentage()}%
            </div>
          </div>
        </div>

        <div className="progress-bar-container">
          <div className="progress-label">
            {t('printingTaskInterface.progress', { percent: ((printingData.printedQuantity / currentRun.targetQuantity) * 100).toFixed(1) })}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${Math.min((printingData.printedQuantity / currentRun.targetQuantity) * 100, 100)}%`
              }}
            />
          </div>
        </div>
      </div>

      {/* Quality Checkpoints */}
      <div className="printing-section">
        <h4>{t('printingTaskInterface.qualityCheckpoints')}</h4>
        <div className="checkpoints-list">
          {checkpointStatus.map((checkpoint, index) => (
            <div
              key={index}
              className={`checkpoint-item ${checkpoint.due ? 'due' : ''} ${checkpoint.overdue ? 'overdue' : ''} ${checkpoint.completed ? 'completed' : ''}`}
            >
              <div className="checkpoint-info">
                <span className="checkpoint-interval">{t('printingTaskInterface.checkpointInterval', { count: checkpoint.interval })}</span>
                <span className="checkpoint-description">{t(`printingTaskInterface.checkpoints.${checkpoint.key}`, { defaultValue: checkpoint.description })}</span>
              </div>
              <div className="checkpoint-actions">
                {!readOnly && checkpoint.due && !checkpoint.completed && (
                  <>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleQualityCheck(index, true, '')}
                    >
                      ✓ {t('printingTaskInterface.pass')}
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => {
                        const notes = prompt(t('printingTaskInterface.issueNotesPrompt'));
                        if (notes !== null) {
                          handleQualityCheck(index, false, notes);
                        }
                      }}
                    >
                      ⚠ {t('printingTaskInterface.issue')}
                    </button>
                  </>
                )}
                {checkpoint.completed && (
                  <span className={`status-badge ${checkpoint.passed ? 'passed' : 'failed'}`}>
                    {checkpoint.passed ? `✓ ${t('printingTaskInterface.passed')}` : `⚠ ${t('printingTaskInterface.issue')}`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality Issues */}
      <div className="printing-section">
        <h4>{t('printingTaskInterface.qualityIssues')}</h4>
        <textarea
          value={printingData.qualityIssues}
          onChange={(e) => handleInputChange('qualityIssues', e.target.value)}
          placeholder={t('printingTaskInterface.qualityIssuesPlaceholder')}
          className="quality-textarea"
          rows="3"
          disabled={readOnly}
        />
      </div>

      {/* Control Buttons */}
      {!readOnly && <div className="control-section">
        <div className="print-controls">
          {printStatus === 'printing' && (
            <>
              <button className="btn btn-warning" onClick={handlePausePrinting}>
                ⏸️ {t('buttons.pause')}
              </button>
              <button className="btn btn-danger" onClick={handleStopPrinting}>
                ⏹️ {t('buttons.stop')}
              </button>
            </>
          )}
          {printStatus === 'paused' && (
            <>
              <button className="btn btn-success" onClick={handleResumePrinting}>
                ▶️ {t('buttons.resume')}
              </button>
              <button className="btn btn-danger" onClick={handleStopPrinting}>
                ⏹️ {t('buttons.stop')}
              </button>
            </>
          )}
          {printStatus === 'completed' && (
            <button
              className={`btn btn-primary btn-lg ${printingData.printedQuantity === 0 ? 'disabled' : ''}`}
              onClick={handleCompletePrinting}
              disabled={printingData.printedQuantity === 0}
            >
              ✅ {t('printingTaskInterface.completeTask')}
            </button>
          )}
        </div>
      </div>}

      <style jsx>{`
        .printing-task-interface {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #28a745;
        }

        .task-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.3em;
        }

        .task-type-badge.printing {
          background: #28a745;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85em;
          font-weight: bold;
          text-transform: uppercase;
        }

        .status-indicator {
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-weight: bold;
          font-size: 0.9em;
        }

        .printing-section {
          margin-bottom: 25px;
          background: white;
          padding: 18px;
          border-radius: 6px;
          border-left: 4px solid #28a745;
        }

        .printing-section h4 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 1.1em;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .section-header h4 {
          margin: 0;
        }

        .session-tracking-section {
          border-left: 4px solid #17a2b8;
        }

        .roll-tracking-section {
          border-left: 4px solid #007bff;
        }

        .session-form {
          background: #e8f7f9;
          border: 2px solid #17a2b8;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .session-form h5 {
          margin-top: 0;
          color: #17a2b8;
        }

        .session-auto-header {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 14px;
          padding: 10px 12px;
          background: #d1ecf1;
          border-radius: 4px;
          font-size: 13px;
          color: #0c5460;
        }

        .session-info-chip {
          white-space: nowrap;
        }

        .session-info-chip + .session-info-chip::before {
          content: '·';
          margin-right: 8px;
          color: #6c757d;
        }

        .shift-radios {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: normal;
          cursor: pointer;
        }

        .sessions-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .sessions-table th {
          background: #f8f9fa;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }

        .sessions-table td {
          padding: 10px;
          border-bottom: 1px solid #dee2e6;
        }

        .sessions-table tfoot td {
          border-top: 2px solid #dee2e6;
          border-bottom: none;
          background: #f8f9fa;
        }

        .totals-row td {
          font-weight: bold;
        }

        .shift-badge {
          display: inline-block;
          padding: 2px 7px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          background: #17a2b8;
          color: white;
        }

        .btn-session {
          background: #17a2b8;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-session:hover:not(:disabled) {
          background: #138496;
          transform: translateY(-1px);
        }

        .btn-session:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-sm.btn-session {
          padding: 6px 12px;
          font-size: 12px;
        }

        .roll-summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }

        .summary-card {
          background: #f8f9fa;
          border-radius: 6px;
          padding: 12px;
          text-align: center;
          border: 1px solid #e9ecef;
        }

        .summary-card.warning {
          background: #fff3cd;
          border-color: #ffc107;
        }

        .summary-label {
          font-size: 11px;
          color: #6c757d;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .summary-value {
          font-size: 1.3em;
          font-weight: bold;
          color: #2c3e50;
        }

        .roll-form {
          background: #f0f8ff;
          border: 2px solid #007bff;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .roll-form h5 {
          margin-top: 0;
          color: #007bff;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 15px;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field.full-width {
          grid-column: 1 / -1;
        }

        .form-field label {
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 5px;
          color: #495057;
        }

        .form-field small {
          font-size: 11px;
          color: #6c757d;
          margin-top: 3px;
        }

        .form-input, .form-select, .form-textarea {
          padding: 8px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .rolls-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .rolls-table th {
          background: #f8f9fa;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }

        .rolls-table td {
          padding: 10px;
          border-bottom: 1px solid #dee2e6;
        }

        .waste-cell {
          font-weight: bold;
          color: #dc3545;
        }

        .category-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .category-badge.setup {
          background: #ffc107;
          color: #212529;
        }

        .category-badge.quality_defect {
          background: #dc3545;
          color: white;
        }

        .category-badge.trim_scrap {
          background: #6c757d;
          color: white;
        }

        .category-badge.other {
          background: #17a2b8;
          color: white;
        }

        .btn-icon {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          padding: 4px 8px;
          margin: 0 2px;
        }

        .btn-icon:hover {
          transform: scale(1.2);
        }

        .btn-icon.danger:hover {
          color: #dc3545;
        }

        .empty-state {
          text-align: center;
          padding: 30px;
          color: #6c757d;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .setting-field label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
        }

        .setting-select, .setting-input, .quantity-input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .production-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .production-metric {
          text-align: center;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .production-metric label {
          display: block;
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .metric-value {
          font-size: 1.5em;
          font-weight: bold;
          color: #2c3e50;
        }

        .metric-value.warning {
          color: #dc3545;
        }

        .quantity-input {
          font-size: 1.2em;
          font-weight: bold;
          text-align: center;
          border: 2px solid #28a745;
        }

        .progress-bar-container {
          margin-top: 15px;
        }

        .progress-label {
          font-size: 14px;
          color: #6c757d;
          margin-bottom: 8px;
        }

        .progress-bar {
          width: 100%;
          height: 12px;
          background: #e9ecef;
          border-radius: 6px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #28a745, #20c997);
          transition: width 0.3s ease;
        }

        .checkpoints-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .checkpoint-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          background: white;
        }

        .checkpoint-item.due {
          border-color: #ffc107;
          background: #fff8e1;
        }

        .checkpoint-item.overdue {
          border-color: #dc3545;
          background: #f8d7da;
        }

        .checkpoint-item.completed {
          border-color: #28a745;
          background: #d4edda;
        }

        .checkpoint-info {
          display: flex;
          flex-direction: column;
        }

        .checkpoint-interval {
          font-size: 12px;
          color: #6c757d;
          font-weight: 500;
        }

        .checkpoint-description {
          font-size: 14px;
          color: #2c3e50;
        }

        .checkpoint-actions {
          display: flex;
          gap: 8px;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }

        .btn-lg {
          padding: 15px 30px;
          font-size: 16px;
        }

        .btn-success {
          background: #28a745;
          color: white;
        }

        .btn-warning {
          background: #ffc107;
          color: #212529;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn:hover:not(.disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
        }

        .status-badge.passed {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.failed {
          background: #f8d7da;
          color: #721c24;
        }

        .quality-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          resize: vertical;
        }

        .control-section {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #dee2e6;
        }

        .print-controls {
          display: flex;
          justify-content: center;
          gap: 15px;
        }

        .instructions {
          background: #e3f2fd;
          padding: 15px;
          border-radius: 6px;
          margin-top: 10px;
          border-left: 4px solid #2196f3;
        }

        .instructions h4 {
          margin-top: 0;
          color: #1976d2;
        }
      `}</style>
    </div>
  );
};

export default PrintingTaskInterface;
